import {DateValue, parseAbsoluteToLocal} from '@internationalized/date';
import {Fragment, memo, useMemo} from 'react';
import {shallowEqual} from '@ui/utils/shallow-equal';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {useUserTimezone} from '@ui/i18n/use-user-timezone';
import {Trans} from '@ui/i18n/trans';

const DIVISIONS: {amount: number; name: Intl.RelativeTimeFormatUnit}[] = [
  {amount: 60, name: 'seconds'},
  {amount: 60, name: 'minutes'},
  {amount: 24, name: 'hours'},
  {amount: 7, name: 'days'},
  {amount: 4.34524, name: 'weeks'},
  {amount: 12, name: 'months'},
  {amount: Number.POSITIVE_INFINITY, name: 'years'},
];

interface FormattedDateProps {
  date?: string | DateValue | Date;
  style?: Intl.RelativeTimeFormatStyle;
}

function toJsDate(
  value: string | DateValue | Date,
  timezone: string,
): Date | null {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string') {
    const raw = value.trim();
    if (!raw) return null;

    // Preferred: full ISO absolute format with timezone (e.g. "2026-02-21T10:11:12Z")
    try {
      return parseAbsoluteToLocal(raw).toDate();
    } catch (e) {
      // fallback below
    }

    // Fallback: MySQL datetime string without timezone.
    if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/.test(raw)) {
      const normalized = raw
        .replace(' ', 'T')
        // JS Date supports milliseconds; trim microseconds if present.
        .replace(/(\.\d{3})\d+$/, '$1');
      // Prefer local parse first to avoid fixed +3h offset on servers
      // that already serialize naive datetimes in local/app timezone.
      const localDate = new Date(normalized);
      if (!Number.isNaN(localDate.getTime())) {
        return localDate;
      }
      // Final fallback: treat as UTC if local parse fails.
      const utcDate = new Date(`${normalized}Z`);
      if (!Number.isNaN(utcDate.getTime())) {
        return utcDate;
      }
      return null;
    }

    const nativeDate = new Date(raw);
    return Number.isNaN(nativeDate.getTime()) ? null : nativeDate;
  }

  try {
    const date = value.toDate(timezone);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch (e) {
    return null;
  }
}

export const FormattedRelativeTime = memo(
  ({date, style}: FormattedDateProps) => {
    const {localeCode} = useSelectedLocale();
    const timezone = useUserTimezone();

    const formatter = useMemo(
      () =>
        new Intl.RelativeTimeFormat(localeCode, {
          numeric: 'auto',
          style,
        }),
      [localeCode, style],
    );

    if (!date) {
      return null;
    }

    const normalizedDate = toJsDate(date, timezone);
    if (!normalizedDate) {
      return null;
    }

    let duration = (normalizedDate.getTime() - Date.now()) / 1000;

    for (let i = 0; i <= DIVISIONS.length; i++) {
      const division = DIVISIONS[i];
      if (Math.abs(duration) < division.amount) {
        if (division.name === 'seconds') {
          return <Trans message="a few seconds ago" />;
        }
        return (
          <Fragment>
            {formatter.format(Math.round(duration), division.name)}
          </Fragment>
        );
      }
      duration /= division.amount;
    }

    return <Fragment>{formatter.format(Math.round(duration), 'day')}</Fragment>;
  },
  shallowEqual,
);
